/*
 * Lulevi personalization.
 * Lives inside the product form. Options are accessible radio grids (swatches /
 * thumbnails / font samples) that scale via search + "show all". A live preview is
 * drawn on the product mockup: the primary text field in the chosen font + color at
 * print_area, and the chosen design image OR the customer's uploaded photo at
 * photo_area. The photo is the "upload your own" tile inside the design picker.
 *
 * Photo upload uses Path A: with a file attached the form submits natively so
 * Shopify keeps the file on the order. Required text fields block add-to-cart via
 * aria-disabled (honoured by Dawn's product-form.js); we only clear flags we set.
 */
if (!customElements.get('lulevi-personalization')) {
  // a character no print can take: emoji and pictographs (each phone draws its own), flags, private-use, joiners, and
  // scripts none of the shop's fonts have (CJK, Arabic…); a glyph missing from one font is lulevi-3d's check
  const BAD_CHAR = /[\p{Extended_Pictographic}\p{Regional_Indicator}\p{Co}\p{Cs}‍️⃣]|[^\p{Script=Latin}\p{Script=Cyrillic}\p{Script=Greek}\p{Script=Common}\p{Script=Inherited}]/u;

  class LuleviPersonalization extends HTMLElement {
    connectedCallback() {
      this.form = this.closest('form');
      // Enter (the phone keyboard's Go / Done) in a text field must not add to cart before colours and fonts are chosen
      // (user 2026-10-09): it moves to the next field, or closes the keyboard on the last one
      if (this.form && !this.form.dataset.luleviEnter) {
        this.form.dataset.luleviEnter = '1';
        this.form.addEventListener('keydown', (e) => {
          var t = e.target;
          if (e.key !== 'Enter' || e.isComposing || !t.matches || !t.matches('input[type="text"], input[type="search"], input:not([type])')) return;
          e.preventDefault();
          var fields = Array.from(this.form.querySelectorAll('input[type="text"], input:not([type])')).filter((f) => f.offsetParent && !f.disabled);
          var next = fields[fields.indexOf(t) + 1];
          if (next) next.focus(); else t.blur();
        });
      }
      this.submitButton = this.form
        ? this.form.querySelector('[type="submit"][name="add"]')
        : null;
      this.colorTarget = this.dataset.colorTarget || 'text';
      this.sectionId = this.dataset.sectionId || '';

      this.inputs = Array.from(this.querySelectorAll('[data-lulevi-input]'));
      this.primaryInput = this.querySelector('[data-lulevi-primary]') || this.inputs[0] || null;
      var previewScope = document.querySelector('[data-lulevi-preview-scope="' + this.sectionId + '"]') || this;
      // Re-parent the overlay onto the actual image frame so its %-coords map to the
      // image (not the wider gallery <li>, which pushed the text to the side).
      if (previewScope !== this && !previewScope.dataset.luleviAnchored) {
        var slide = previewScope.closest('.product__media-item');
        var frame = slide && (slide.querySelector('.product__media.media') || slide.querySelector('.product-media-container'));
        if (frame) {
          frame.style.position = 'relative';
          frame.appendChild(previewScope);
          previewScope.dataset.luleviAnchored = '1';
        }
      }
      this.previewText = previewScope.querySelector('[data-lulevi-preview-text]');
      this.previewImage = previewScope.querySelector('[data-lulevi-preview-image]');
      this.stage = previewScope.querySelector('.lulevi-preview__stage') || previewScope;
      // The shopper may leaf through the photos; any change in the editor brings the live preview's slide back.
      this.previewScope = previewScope;
      this.addEventListener('input', () => this.showPreview());
      this.addEventListener('change', () => this.showPreview());
      this.photoField = this.querySelector('[data-lulevi-photo-field]');
      this.photoInput = this.querySelector('[data-lulevi-photo]');
      this.designImageUrl = '';
      this.photoActive = false;
      this.photos = []; // [{ input, url, point }]: one file input per photo, so each lands on the order
      this.photoSeq = 1;
      this.maxPhotos = parseInt((this.photoField && this.photoField.dataset.maxPhotos) || '1', 10);
      this.picks = this.querySelector('[data-lulevi-photo-picks]');

      this.inputs.forEach((input) => {
        input.addEventListener('input', () => this.onInput(input));
        this.onInput(input, true);
      });
      // an optional text line (a date, a few words) waits behind «+ Second line (optional)» until it's wanted: one field on
      // show instead of two (owner 2026-10-27, the simpler phone editor). A value (the edit link, autofill) opens it
      this.inputs.forEach((input) => {
        const field = input.closest('[data-lulevi-field]'), label = field && field.querySelector('.lulevi-field__label');
        if (input === this.primaryInput || input.hasAttribute('data-lulevi-required') || input.value || !label || field.hidden || field.hasAttribute('data-lulevi-only')) return;
        const add = (input.luleviAdd = document.createElement('button'));
        add.type = 'button';
        add.className = 'lulevi-field__add';
        add.textContent = '+ ' + label.textContent.trim().split(':')[0];
        add.addEventListener('click', () => { this.unfold(input); input.focus(); });
        field.before(add);
        field.hidden = true;
      });
      // the font tiles write the typed name instead of «Ab» (owner 2026-10-27). The tiles lay out only near the screen
      // (content-visibility), so the full fonts a name needs download there; no name: the field's example in the tiles' light fonts
      this.fontTiles = Array.from(this.querySelectorAll('.lulevi-opt__fontpreview'));
      if (this.fontTiles.length) {
        if (window.IntersectionObserver) {
          const near = new IntersectionObserver((seen) => {
            if (!seen.some((e) => e.isIntersecting)) return;
            this.fitFonts();
            setTimeout(() => this.fitFonts(), 600);
          });
          new Set(this.fontTiles.map((tile) => tile.closest('.lulevi-opt__grid'))).forEach((grid) => near.observe(grid)); // the name letter has two
        }
        if (document.fonts) document.fonts.addEventListener('loadingdone', () => this.fitFonts()); // the full fonts come late
      }

      Array.from(this.querySelectorAll('[data-lulevi-opt]')).forEach((grid) => this.initGrid(grid));

      if (this.photoInput) {
        this.photoInput.addEventListener('change', () => this.onPhoto(this.photoInput));
        const addPhoto = this.querySelector('[data-lulevi-photo-add]');
        if (addPhoto) addPhoto.addEventListener('click', () => this.addPhoto());
        this.form.setAttribute('enctype', 'multipart/form-data');
      }
      if (this.form) {
        if (this.submitButton) {
          this.submitButton.addEventListener(
            'click',
            (event) => {
              const files = Array.from(this.form.querySelectorAll('input[type="file"]')).some((i) => !i.disabled && i.files && i.files.length);
              const preview = typeof this.form.luleviBeforeSubmit === 'function'; // lulevi-3d: the preview as the cart picture
              if (!files && !preview) return; // nothing attached: Dawn's ajax add-to-cart as usual
              event.preventDefault();
              event.stopImmediatePropagation();
              if (!this.isValid()) {
                this.showErrors();
                return;
              }
              this.submitButton.classList.add('loading', 'lulevi-saving');
              this.submitButton.setAttribute('aria-busy', 'true');
              // lulevi-3d's cart picture first (the print files are drawn in the workshop from the order), never more than 5 s, then sent
              const ready = preview ? Promise.race([this.form.luleviBeforeSubmit(), new Promise((r) => setTimeout(r, 5000))]) : true;
              Promise.resolve(ready).catch(() => {}).then(() => this.send());
            },
            true
          );
          // "Saving your design…" on the button instead of a bare spinner, through the upload too (Dawn's product-form adds
          // and removes "loading" round its fetch: when it goes after send(), the text goes)
          const saving = document.createElement('span');
          saving.className = 'lulevi-atc-saving';
          saving.textContent = this.dataset.msgSaving || '';
          this.submitButton.appendChild(saving);
          new MutationObserver(() => {
            if (this.sent && !this.submitButton.classList.contains('loading')) {
              this.sent = false;
              this.submitButton.classList.remove('lulevi-saving');
            }
          }).observe(this.submitButton, { attributes: true, attributeFilter: ['class'] });
        }
      }

      if (this.form) {
        this.form.addEventListener('submit', () => {
          if (!this.isValid()) this.showErrors();
        });
        // capture: before Dawn's product-form reads the form, the "Edit" link for the cart line is filled in
        this.form.addEventListener('submit', () => this.rememberEdit(), true);
        // what is sent (Dawn's new FormData(form), or the native submit): the shopper's language for checkout and emails
        this.form.addEventListener('formdata', (e) => this.localize(e.formData));
      }

      this.alwaysOn = this.dataset.alwaysOn === 'true';
      try {
        this.personalizedVariants = JSON.parse(this.dataset.personalizedVariants || '[]').map(String);
      } catch (error) {
        this.personalizedVariants = [];
      }
      this.idInput = this.form ? this.form.querySelector('[name="id"]') : null;
      if (!this.alwaysOn && this.form) {
        this.form.addEventListener('change', () => setTimeout(() => this.updateActive(), 0));
      }

      this.onlyBlocks = Array.from(this.querySelectorAll('[data-lulevi-only]'));
      if (this.onlyBlocks.length && this.form) {
        this.form.addEventListener('change', (e) => { if (e.target.name === 'id') this.updateOnly(); });
      }

      this.renderPreviewText();
      this.updateActive();
      this.pinning();
      const restore = () => setTimeout(() => this.restoreEdit(), 0); // after the other widgets have wired their listeners
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', restore, { once: true });
      else restore();
    }

    // Add to cart with the files (the cart picture, photos): Dawn's product-form posts the whole form, files
    // included (checked on the store), and opens the cart drawer without leaving the page. Without it: the native
    // multipart POST to /cart/add and the cart page, as before.
    send() {
      this.rememberEdit(); // form.submit() fires no submit event
      this.sent = true;
      this.submitButton.classList.remove('loading');
      this.submitButton.removeAttribute('aria-busy');
      if (this.form.closest('product-form') && typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      else this.form.submit();
    }

    // The cart's "Edit" link (hidden property _edit): this page with the variant and every choice, so it opens as it was.
    // The workshop draws the order's prints from it (render_prints.mjs), the photos from the order with _Photo points.
    rememberEdit() {
      if (!this.active || !this.form) return;
      const picks = {};
      Array.from(this.form.elements).forEach((el) => {
        const name = el.name || '';
        if (!name.startsWith('properties[') || name.startsWith('properties[_') || el.disabled || el.type === 'file') return;
        if ((el.type === 'radio' || el.type === 'checkbox') && !el.checked) return;
        if (el.value === '') return;
        picks[name] = el.value;
      });
      if (!this.editInput) {
        this.editInput = document.createElement('input');
        this.editInput.type = 'hidden';
        this.editInput.name = 'properties[_edit]';
        this.form.appendChild(this.editInput);
      }
      const bubble = this.querySelector('lulevi-bubble'); // its letter colours come back through its own ?pattern=&colors=
      // the background (lulevi-pattern-grid: no properties[] radios, only the hidden _Background) through ?pattern=&palette=, as the production link
      const bg = this.querySelector('[data-lulevi-pattern]:not([hidden])'), tile = bg && bg.querySelector('[data-pattern]:checked'), pal = bg && bg.querySelector('[data-palette]:checked');
      this.editInput.value =
        location.pathname + '?variant=' + (this.idInput ? this.idInput.value : '') +
        (bubble && typeof bubble.params === 'function' ? bubble.params() : '') +
        (tile ? '&pattern=' + encodeURIComponent(tile.dataset.pattern) + (pal ? '&palette=' + encodeURIComponent(pal.dataset.palette) : '') : '') +
        (this.photos.length ? '&photo=1' : '') +
        '&edit=' + encodeURIComponent(JSON.stringify(picks));
      // the point tapped on each photo (what the cut-out keeps), in the preview's order, by field: «Photo:0.3444,0.4405;Photo 2:…»
      // (the order lists the photos in the shopper's language, «Foto 2»: the number stays)
      if (!this.pointsInput) {
        this.pointsInput = document.createElement('input');
        this.pointsInput.type = 'hidden';
        this.pointsInput.name = 'properties[_Photo points]';
        this.form.appendChild(this.pointsInput);
      }
      this.pointsInput.value = this.photos.map((p) => p.input.name.slice(11, -1) + ':' + p.point.x.toFixed(4) + ',' + p.point.y.toFixed(4)).join(';');
      this.pointsInput.disabled = !this.photos.length;
    }

    // Checkout and the order emails list the visible properties as they are (QA 2026-10-10: «Colour: 21 — Caramelo»,
    // «Country: E»): each goes as the shopper reads it, the key in their language and the value without the shop's codes
    // («Color: Caramelo», «País: España (E)»), and as a hidden copy with the English key and the full value for the
    // workshop and the order scripts («_Colour: 21 — Caramelo»). The fields keep their English names, so the Edit link and
    // older cart lines work as before. A photo goes once, under the visible key (two copies would upload it twice).
    localize(fd) {
      if (!this.active) return;
      let labels = {};
      try { labels = JSON.parse(this.dataset.propLabels || '{}'); } catch (e) { labels = {}; }
      // as lulevi-prop-label / lulevi-prop-value do in the cart: "Photo 2", "(uv)" kept; numbers between " — " dropped
      const label = (key) => {
        const base = key.split(' (')[0], photo = /^Photo( \d+)$/.exec(base);
        return (photo ? (labels.Photo || 'Photo') + photo[1] : labels[base] || base) + key.slice(base.length);
      };
      const clean = (v) => v.split(' · ').map((part) => part.split(' — ').map((x) => x.trim()).filter((x) => !/^\d+(\.\d+)?$/.test(x)).join(': ') || part).join(' · ');
      const props = Array.from(fd.entries()).filter(([name]) => /^properties\[.+\]$/.test(name)), used = new Set();
      props.forEach(([name]) => fd.delete(name));
      props.forEach(([name, value]) => {
        const key = name.slice(11, -1);
        if (key[0] === '_') return fd.append(name, value);
        if (value === '' || (typeof value !== 'string' && !value.size)) return; // an optional field left empty (Line 2), no photo: no blank line in checkout
        // two keys with one translation (es: Design and Pattern are both «Diseño») would merge into one line: the second keeps its key
        let shownKey = label(key);
        if (used.has(shownKey)) shownKey = key;
        used.add(shownKey);
        if (typeof value !== 'string') return fd.append('properties[' + shownKey + ']', value);
        const el = this.form.elements.namedItem(name);
        const shown = el && el.type === 'text' ? value : el && el.tagName === 'SELECT' && el.selectedOptions[0] ? el.selectedOptions[0].textContent.trim() : clean(value);
        fd.append('properties[' + shownKey + ']', shown);
        fd.append('properties[_' + key + ']', value);
      });
    }

    // ?edit=… : the choices back (radios checked, texts filled, each with its event so previews follow).
    // ?replace=<cart line key>: once the edited item is in the cart, the old line goes.
    restoreEdit() {
      const q = new URLSearchParams(location.search);
      let picks = null;
      try { picks = JSON.parse(q.get('edit') || 'null'); } catch (e) { picks = null; }
      if (picks && this.form) {
        Object.keys(picks).forEach((name) => {
          const item = this.form.elements.namedItem(name);
          if (!item) return;
          const els = typeof RadioNodeList !== 'undefined' && item instanceof RadioNodeList ? Array.from(item) : [item];
          const radios = els.filter((el) => el.type === 'radio');
          let pick = radios.find((el) => el.value === picks[name]);
          // an option renamed since the link was made (a design or colour name translated: «1 — Ballerinas 1» → «1 — Bailarinas 1»):
          // the same number, unique within a picker (checked over all 22 design sets, 2026-10-22)
          const num = !pick && /^(\d+) — /.exec(picks[name] || '');
          if (num) pick = radios.find((el) => el.value.startsWith(num[1] + ' — '));
          els.forEach((el) => {
            if (el.type === 'radio') {
              if (el === pick && !el.checked) { el.checked = true; el.dispatchEvent(new Event('change', { bubbles: true })); }
            } else if (el.type === 'checkbox') {
              if (el.value === picks[name] && !el.checked) { el.checked = true; el.dispatchEvent(new Event('change', { bubbles: true })); }
            } else if (el.type !== 'file' && el.type !== 'hidden') {
              el.value = picks[name];
              el.dispatchEvent(new Event('input', { bubbles: true }));
              el.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        });
      }
      // a photo can't travel in the edit link (rememberEdit marks it): ask for it again
      const msg = this.querySelector('[data-lulevi-photo-msg]');
      if (q.get('photo') && msg && this.photoField) {
        this.photoField.hidden = false;
        msg.textContent = this.photoField.dataset.msgReadd || '';
        msg.hidden = false;
      }
      const replace = q.get('replace');
      if (!replace || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      const off = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (!event || event.source !== 'product-form' || !event.cartData || event.cartData.key === replace) return;
        off();
        const drawer = document.querySelector('cart-drawer');
        const body = { id: replace, quantity: 0 };
        if (drawer && drawer.getSectionsToRender) {
          body.sections = drawer.getSectionsToRender().map((section) => section.id);
          body.sections_url = location.pathname;
        }
        fetch(((window.Shopify && Shopify.routes && Shopify.routes.root) || '/') + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        })
          .then((r) => r.json())
          .then((state) => { if (drawer && state && state.sections) drawer.renderContents(state); })
          .catch(() => {});
        const url = new URL(location.href);
        url.searchParams.delete('replace');
        history.replaceState(history.state, '', url);
      });
    }

    // Phones (lulevi.css): the preview pinned on top lets go once the add-to-cart button has scrolled past, gets smaller
    // once the page is scrolled into the options (lulevi-compact), and smaller still while a text field has focus (the
    // keyboard takes half the screen).
    pinning() {
      const wrap = this.closest('.product') && this.closest('.product').querySelector('.product__media-wrapper');
      this.addEventListener('focusin', (e) => { if (e.target.matches('[data-lulevi-input]')) document.body.classList.add('lulevi-typing'); });
      // A tap elsewhere blurs the field on its mousedown; growing the preview right then moved the tapped button or tile
      // away before its click (iPhone: the first tap after typing was lost), so the preview grows back once the tap is over.
      let grow = 0;
      this.addEventListener('focusout', () => {
        clearTimeout(grow);
        grow = setTimeout(() => {
          const a = document.activeElement;
          if (!a || !a.matches('[data-lulevi-input]')) document.body.classList.remove('lulevi-typing');
        }, 400);
      });
      if (!wrap || !this.submitButton) return;
      // a scroll check, not an IntersectionObserver: a flick jumps the button from below the screen to above it unseen.
      // The height the smaller preview gives up stays as a margin under it, so nothing below moves (a scroll correction
      // would stop an iPhone flick); while typing it stays as it is
      const product = wrap.closest('.product');
      let queued = false, compact = false;
      const shrink = (on) => {
        if (on === compact) return;
        compact = on;
        wrap.style.marginBottom = '';
        const full = wrap.getBoundingClientRect().height; // with its fraction: 28vh isn't whole pixels
        document.body.classList.toggle('lulevi-compact', on);
        if (on) wrap.style.marginBottom = full - wrap.getBoundingClientRect().height + 'px';
      };
      const check = () => {
        queued = false;
        wrap.classList.toggle('lulevi-unpinned', this.submitButton.getBoundingClientRect().bottom < 0);
        if (!document.body.classList.contains('lulevi-typing')) shrink(window.scrollY > product.getBoundingClientRect().top + window.scrollY + 80);
      };
      window.addEventListener('scroll', () => { if (!queued) { queued = true; requestAnimationFrame(check); } }, { passive: true });
      window.addEventListener('resize', () => { shrink(false); check(); });
    }

    updateOnly() {
      const id = this.idInput ? String(this.idInput.value) : '';
      this.onlyBlocks.forEach((block) => {
        const on = this.active && block.dataset.luleviOnly.split(',').indexOf(id) !== -1;
        block.hidden = !on;
        block.querySelectorAll('input, textarea, select').forEach((el) => {
          el.disabled = !on;
        });
      });
      this.updateValidity();
    }

    updateActive() {
      let active = this.alwaysOn;
      if (!active && this.idInput) {
        active = this.personalizedVariants.indexOf(String(this.idInput.value)) !== -1;
      }
      this.active = active;
      this.hidden = !active;
      this.querySelectorAll('input, textarea, select').forEach((el) => {
        el.disabled = !active;
      });
      if (this.onlyBlocks && this.onlyBlocks.length) this.updateOnly();
      else this.updateValidity();
    }

    /* ---- text fields ---- */
    onInput(input, init) {
      const field = input.closest('[data-lulevi-field]');
      const counter = field.querySelector('[data-lulevi-counter]');
      const max = parseInt(input.getAttribute('maxlength') || '0', 10), len = input.value.length;
      if (input.luleviAdd && input.value) this.unfold(input);
      if (counter && counter.dataset.luleviPrice === undefined) { // the bubble letters' name field says its price instead
        // read out only near the limit, not on every letter
        counter.setAttribute('aria-live', max && len >= max - 3 ? 'polite' : 'off');
        counter.textContent = `${len}/${max || ''}` + (max && len >= max ? ' · ' + (this.dataset.msgMax || '') : '');
      }
      if (!init) {
        field.classList.remove('lulevi-field--error');
        input.setAttribute('aria-invalid', 'false');
        const error = field.querySelector('[data-lulevi-error]');
        if (error) error.hidden = true;
      }
      if (input === this.primaryInput) { this.renderPreviewText(); this.fitFonts(); }
      this.flagChars(input, 'emoji', [...new Set([...input.value].filter((ch) => BAD_CHAR.test(ch)))]);
    }

    unfold(input) {
      input.luleviAdd.remove();
      input.luleviAdd = null;
      input.closest('[data-lulevi-field]').hidden = false;
    }

    // the name in every font tile: the whole name, smaller if it must, with room at the sides (a script's swashes reach past
    // the text's own box and were cut at the tile's edges)
    fitFonts() {
      if (!this.fontTiles || !this.fontTiles.length) return;
      const input = this.primaryInput, typed = input ? input.value.trim() : '', sample = input ? input.dataset.luleviSample || '' : '';
      // no name yet: the field's example («Lucía», as the preview shows it, owner 2026-10-27) when the light fonts have its
      // letters (LIGHT = .playwright-mcp/ai3d/font_ab.py), else «Ab»: another example (Emma, Sofía) would fetch the full fonts
      const LIGHT = 'AbLucía', name = typed || ([...sample].every((ch) => LIGHT.includes(ch)) ? sample : ''), range = document.createRange();
      this.classList.toggle('lulevi-named', !!name); // the tiles' font names: for screen readers only while the name shows
      this.fontTiles.forEach((tile) => {
        // a typed name in the full font only: its letters from two files broke a script's joins (Martina's «a» from the light one)
        tile.luleviFont = tile.luleviFont || tile.style.fontFamily;
        tile.style.fontFamily = typed ? tile.luleviFont.replace(/^[^,]*,\s*/, '') : tile.luleviFont;
        const big = tile.closest('[data-lulevi-opt="letterfont"]'); // the name letter's own letter: its style tiles show the name's first letter
        const text = name ? (big ? [...name][0].toUpperCase() : name) : 'Ab';
        if (tile.textContent !== text) tile.textContent = text;
        tile.style.fontSize = '';
        if (!name || big || !tile.clientWidth) return;
        let size = 26;
        tile.style.fontSize = size + 'px';
        range.selectNodeContents(tile);
        while (range.getBoundingClientRect().width > tile.clientWidth * 0.84 && size > 12) tile.style.fontSize = --size + 'px';
      });
    }

    // One check for every editor: what a print can't take blocks add to cart with a message, instead of a box, a gap or
    // the phone's own emoji in the print file. source: 'emoji' (here: emoji, symbols, scripts none of our fonts have),
    // 'font' (lulevi-3d: glyphs the chosen font and its fallback lack), 'bubble' (its alphabet). The first source with
    // something to say shows; msg: its own text with [chars].
    flagChars(input, source, chars, msg) {
      if (!input) return;
      const all = (input.luleviBad = input.luleviBad || {});
      all[source] = chars && chars.length ? { chars, msg: msg || this.dataset.msgChars || '[chars]' } : null;
      const hit = Object.values(all).find(Boolean);
      const field = input.closest('[data-lulevi-field]'), err = field && field.querySelector('[data-lulevi-error]');
      if (err && err.dataset.text === undefined) err.dataset.text = err.textContent;
      if (hit) {
        const shown = hit.chars.filter((ch) => /\S/.test(ch) && !/[‍️⃣]/.test(ch));
        const text = hit.msg.replace('[chars]', (shown.length ? shown : hit.chars).join(' '));
        input.dataset.luleviBad = text;
        if (err && (err.textContent !== text || err.hidden)) { err.textContent = text; err.hidden = false; }
        if (field) field.classList.add('lulevi-field--error');
        input.setAttribute('aria-invalid', 'true');
      } else if (input.dataset.luleviBad !== undefined) {
        delete input.dataset.luleviBad;
        if (err) { err.textContent = err.dataset.text; err.hidden = true; }
        if (field) field.classList.remove('lulevi-field--error');
        input.setAttribute('aria-invalid', 'false');
      }
      this.updateValidity();
    }

    // The live preview's slide back in the gallery: the slide and its thumbnail only (Dawn's setActiveMedia would also
    // scroll the page up to the photo — on a phone that pulls the editor away on every letter typed).
    showPreview() {
      const scope = this.previewScope, slide = scope && scope !== this && scope.closest('[data-media-id]');
      const list = slide && slide.parentElement;
      if (!list || (slide.classList.contains('is-active') && Math.abs(list.scrollLeft - slide.offsetLeft) < 2)) return;
      list.querySelectorAll('[data-media-id]').forEach((el) => el.classList.toggle('is-active', el === slide));
      list.scrollTo({ left: slide.offsetLeft });
      const gallery = slide.closest('media-gallery'), thumbs = gallery && gallery.elements && gallery.elements.thumbnails;
      if (thumbs) gallery.setActiveThumbnail(thumbs.querySelector('[data-target="' + slide.dataset.mediaId + '"]'));
    }

    renderPreviewText() {
      if (!this.previewText || !this.primaryInput) return;
      this.previewText.textContent = this.primaryInput.value;
    }

    /* ---- option grids ---- */
    initGrid(grid) {
      const radios = Array.from(grid.querySelectorAll('[data-lulevi-radio]'));
      const items = Array.from(grid.querySelectorAll('.lulevi-opt__item'));
      const search = grid.querySelector('[data-lulevi-search]');
      const more = grid.querySelector('[data-lulevi-more]');
      const empty = grid.querySelector('[data-lulevi-empty]');

      radios.forEach((radio) => radio.addEventListener('change', () => this.onOption(grid, radio)));

      if (grid.classList.contains('lulevi-opt--many') && more) {
        grid.classList.add('is-collapsed');
        more.hidden = false;
        more.addEventListener('click', () => {
          grid.classList.remove('is-collapsed');
          more.hidden = true;
        });
      }

      if (search) {
        search.addEventListener('input', () => {
          const q = search.value.trim().toLowerCase();
          let visible = 0;
          items.forEach((item) => {
            const match = !q || (item.dataset.search || '').indexOf(q) !== -1;
            item.hidden = !match;
            if (match) visible += 1;
          });
          if (empty) empty.hidden = visible > 0;
          if (q) {
            grid.classList.remove('is-collapsed');
            if (more) more.hidden = true;
          }
        });
      }

      const checked = radios.find((radio) => radio.checked);
      if (checked) this.onOption(grid, checked, true);
    }

    onOption(grid, radio, init) {
      const type = grid.dataset.luleviOpt;
      const selected = grid.querySelector('[data-lulevi-selected]');
      if (selected) {
        const nameEl = radio.closest('.lulevi-opt__item');
        const label = nameEl && nameEl.querySelector('.lulevi-opt__name');
        selected.textContent = label ? label.textContent : radio.value;
      }

      const item = radio.closest('.lulevi-opt__item');
      if (item) {
        const prev = grid.querySelector('.lulevi-opt__item.is-selected');
        if (prev) prev.classList.remove('is-selected');
        item.classList.add('is-selected');
      }

      if (radio.hasAttribute('data-lulevi-upload-trigger')) {
        this.showPhotoField(true);
        if (!init && !this.photos.length && this.photoInput) this.photoInput.click(); // the tap on "+" opens the gallery
        this.designImageUrl = '';
        if (!this.photoActive) this.showOverlayImage('');
        this.emitArt();
        this.updateValidity();
        return;
      }

      if (type === 'design') {
        this.showPhotoField(false);
        this.designImageUrl = radio.dataset.image || '';
        if (!this.photoActive) this.showOverlayImage(this.designImageUrl);
        this.emitArt();
        this.inkFromPicture(this.designImageUrl);
        this.updateValidity();
      } else if (type === 'color') {
        if (!init && !this.autoInk) this.inkTouched = true; // the shopper's own colour stays theirs
        const hex = radio.dataset.hex || '';
        if (this.colorTarget === 'product') this.tintProduct(hex);
        else if (this.previewText) this.previewText.style.color = hex || '';
      } else if (type === 'font') {
        if (this.previewText) this.previewText.style.fontFamily = radio.dataset.fontStack || '';
      }
    }

    // the name's colour picked from the drawing like a pipette (user, 2026-10-06): of the colours offered, the one most of
    // the drawing's readable pixels are closest to — until the shopper picks a colour themselves
    inkFromPicture(url) {
      const grid = this.querySelector('[data-lulevi-opt="color"]');
      if (!url || !grid || this.inkTouched) return;
      const radios = Array.from(grid.querySelectorAll('[data-lulevi-radio][data-hex]')).filter((r) => !/^#?f{3}(f{3})?$/i.test(r.dataset.hex));
      if (radios.length < 2) return;
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          const c = document.createElement('canvas'); c.width = c.height = 48;
          const x = c.getContext('2d'); x.drawImage(img, 0, 0, 48, 48);
          const d = x.getImageData(0, 0, 48, 48).data, sw = radios.map((r) => [1, 3, 5].map((i) => parseInt(r.dataset.hex.slice(i, i + 2), 16)));
          const votes = new Array(radios.length).fill(0);
          for (let i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 200) continue;
            const mx = Math.max(d[i], d[i + 1], d[i + 2]), mn = Math.min(d[i], d[i + 1], d[i + 2]), l = (mx + mn) / 510;
            if (l < 0.18 || l > 0.78 || mx - mn < 38) continue; // paper, outlines and greys don't count
            let bi = 0, bd = Infinity;
            sw.forEach(([r, g, b], j) => { const e = 2 * (d[i] - r) ** 2 + 4 * (d[i + 1] - g) ** 2 + 3 * (d[i + 2] - b) ** 2; if (e < bd) { bd = e; bi = j; } });
            votes[bi]++;
          }
          const best = votes.indexOf(Math.max(...votes));
          if (votes[best] && !radios[best].checked && !this.inkTouched) {
            this.autoInk = true; radios[best].checked = true; radios[best].dispatchEvent(new Event('change', { bubbles: true })); this.autoInk = false;
          }
        } catch (e) { /* a picture without CORS: keep the colour */ }
      };
      img.src = url;
    }

    showPhotoField(show) {
      if (!this.photoField || !this.photoField.hasAttribute('data-lulevi-photo-gated')) return;
      this.photoField.hidden = !show;
      if (!show) {
        this.photos.forEach((p) => {
          if (p.input !== this.photoInput) p.input.remove();
          URL.revokeObjectURL(p.url);
        });
        this.photos = [];
        this.renderPicks();
        if (this.photoInput) this.photoInput.value = '';
        this.photoActive = false;
        this.showOverlayImage(this.designImageUrl);
        const msg = this.querySelector('[data-lulevi-photo-msg]');
        if (msg) msg.hidden = true;
      }
    }

    /* ---- photos: one, or up to data-max-photos for a scene; each keeps the point the shopper tapped (what to cut out) ---- */
    async onPhoto(input) {
      const msgEl = this.querySelector('[data-lulevi-photo-msg]');
      const showMsg = (text) => {
        if (!msgEl) return;
        msgEl.textContent = text || '';
        msgEl.hidden = !text;
      };
      showMsg('');
      const at = this.photos.findIndex((p) => p.input === input);
      const drop = () => {
        input.value = '';
        if (at !== -1) {
          URL.revokeObjectURL(this.photos[at].url);
          this.photos.splice(at, 1);
        }
        if (input !== this.photoInput) input.remove();
        this.photosChanged();
      };

      let file = input.files && input.files[0];
      if (!file) return drop();
      const heic = /^image\/hei[cf]$/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
      const okType = heic || /^image\/(png|jpe?g)$/i.test(file.type) || /\.(png|jpe?g)$/i.test(file.name);
      if (!okType) {
        showMsg(this.photoField.dataset.msgType);
        return drop();
      }
      const maxMb = parseFloat(this.photoInput.dataset.maxMb || '8');
      // HEIC and big photos go as a JPEG made here; what the browser can't open gets a message
      if (heic || file.size > maxMb * 1024 * 1024) {
        const jpeg = await this.shrink(file, maxMb * 1024 * 1024);
        if (!jpeg) {
          showMsg(heic ? this.photoField.dataset.msgHeic : this.photoField.dataset.msgSize);
          return drop();
        }
        try {
          const dt = new DataTransfer();
          dt.items.add(jpeg);
          input.files = dt.files;
          file = jpeg;
        } catch (error) {
          showMsg(this.photoField.dataset.msgSize);
          return drop();
        }
      }

      const url = URL.createObjectURL(file);
      if (at !== -1) {
        URL.revokeObjectURL(this.photos[at].url);
        Object.assign(this.photos[at], { url, point: { x: 0.5, y: 0.5 } });
      } else this.photos.push({ input, url, point: { x: 0.5, y: 0.5 } });
      this.photosChanged();

      const probe = new Image();
      probe.onload = () => {
        if (probe.naturalWidth < 800 || probe.naturalHeight < 800) showMsg(this.photoField.dataset.msgLowres);
      };
      probe.src = url;
    }

    // a photo as a JPEG of at most `max` bytes (4000, 3000 or 2000 px a side), or null if this browser can't open it
    // (HEIC opens in Safari only)
    async shrink(file, max) {
      const url = URL.createObjectURL(file);
      const img = await new Promise((ok) => {
        const i = new Image();
        i.onload = () => ok(i);
        i.onerror = () => ok(null);
        i.src = url;
      });
      URL.revokeObjectURL(url);
      if (!img || !img.naturalWidth) return null;
      for (const side of [4000, 3000, 2000]) {
        const k = Math.min(1, side / Math.max(img.naturalWidth, img.naturalHeight)), c = document.createElement('canvas');
        c.width = Math.round(img.naturalWidth * k);
        c.height = Math.round(img.naturalHeight * k);
        c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
        const blob = await new Promise((ok) => c.toBlob(ok, 'image/jpeg', 0.9));
        if (blob && blob.size <= max) return new File([blob], file.name.replace(/\.[^.]+$/, '') + '.jpg', { type: 'image/jpeg' });
      }
      return null;
    }

    photosChanged() {
      this.photoActive = this.photos.length > 0;
      const named = this.photoField && this.photoField.querySelector('[data-lulevi-photo-name]');
      if (named) named.textContent = this.photos.map((p) => (p.input.files && p.input.files[0] ? p.input.files[0].name : '')).filter(Boolean).join(', ');
      this.showOverlayImage(this.photoActive ? this.photos[0].url : this.designImageUrl);
      this.renderPicks();
      this.emitArt();
      this.updateValidity();
    }

    addPhoto() {
      if (this.photos.length >= this.maxPhotos) return;
      const input = this.photoInput.cloneNode();
      input.removeAttribute('id');
      input.removeAttribute('aria-describedby');
      input.value = '';
      input.name = this.photoInput.name.replace(/\]$/, ' ' + ++this.photoSeq + ']'); // properties[Photo 2], [Photo 3]…
      input.hidden = true;
      input.addEventListener('change', () => this.onPhoto(input));
      this.photoField.appendChild(input);
      input.click();
    }

    // thumbnails of the chosen photos: a tap marks who to cut out (a dot), × removes the photo
    renderPicks() {
      if (!this.picks) return;
      const box = this.picks.querySelector('[data-lulevi-photo-thumbs]');
      this.picks.hidden = !this.photos.length;
      box.textContent = '';
      this.photos.forEach((p) => {
        const wrap = document.createElement('span');
        wrap.className = 'lulevi-photo__pick';
        const pick = document.createElement('button');
        pick.type = 'button';
        pick.className = 'lulevi-photo__img';
        pick.setAttribute('aria-label', this.picks.dataset.tap || '');
        const img = document.createElement('img');
        img.src = p.url;
        img.alt = '';
        pick.appendChild(img);
        pick.addEventListener('click', (event) => {
          if (!event.detail) return; // keyboard: keep the centre
          const r = img.getBoundingClientRect();
          p.point = { x: (event.clientX - r.left) / r.width, y: (event.clientY - r.top) / r.height };
          this.renderPicks();
          this.emitArt();
        });
        const dot = document.createElement('i');
        dot.className = 'lulevi-photo__dot';
        dot.style.left = p.point.x * 100 + '%';
        dot.style.top = p.point.y * 100 + '%';
        const del = document.createElement('button');
        del.type = 'button';
        del.className = 'lulevi-photo__del';
        del.setAttribute('aria-label', this.picks.dataset.remove || '×');
        del.textContent = '×';
        del.addEventListener('click', () => {
          p.input.value = '';
          if (p.input !== this.photoInput) p.input.remove();
          URL.revokeObjectURL(p.url);
          this.photos.splice(this.photos.indexOf(p), 1);
          this.photosChanged();
        });
        wrap.append(pick, dot, del);
        box.appendChild(wrap);
      });
      const add = this.picks.querySelector('[data-lulevi-photo-add]');
      if (add) add.hidden = this.photos.length >= this.maxPhotos;
    }

    // what the print shows: the chosen design, or the customer's photos (lulevi-3d cuts them out); several photos = a scene
    emitArt() {
      const detail = this.photoActive
        ? { photos: this.photos.map((p) => ({ url: p.url, point: p.point })) }
        : { designs: this.designImageUrl ? [this.designImageUrl] : [] };
      this.dispatchEvent(new CustomEvent('lulevi:art', { bubbles: true, detail }));
    }

    showOverlayImage(url) {
      if (!this.previewImage) return;
      if (url) {
        this.previewImage.style.backgroundImage = `url("${url}")`;
        this.previewImage.hidden = false;
      } else {
        this.previewImage.style.backgroundImage = '';
        this.previewImage.hidden = true;
      }
    }

    tintProduct(hex) {
      if (!this.stage) return;
      let layer = this.stage.querySelector('.lulevi-preview__tint');
      if (!layer) {
        layer = document.createElement('div');
        layer.className = 'lulevi-preview__tint';
        this.stage.appendChild(layer);
      }
      layer.style.background = hex || 'transparent';
    }

    /* ---- validity ---- */
    requiredInputs() {
      return this.inputs.filter((input) => input.hasAttribute('data-lulevi-required'));
    }

    isValid() {
      if (!this.active) return true;
      if (this.photoMissing()) return false;
      return this.inputs.every((input) => input.disabled || input.dataset.luleviBad === undefined) &&
        this.requiredInputs().every((input) => input.disabled || input.value.trim().length > 0);
    }

    // the "My photo" tile with no photo (the picker cancelled, or Edit: photos can't come back from the cart)
    photoMissing() {
      const up = this.querySelector('[data-lulevi-upload-trigger]');
      return !!(up && up.checked && !this.photos.length);
    }

    updateValidity() {
      if (!this.submitButton) return;
      if (this.isValid()) {
        if (this.submitButton.dataset.luleviBlocked) {
          this.submitButton.removeAttribute('aria-disabled');
          delete this.submitButton.dataset.luleviBlocked;
        }
      } else {
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.dataset.luleviBlocked = 'true';
      }
    }

    showErrors() {
      let first = null;
      // empty, or characters we can't make (flagChars)
      this.inputs
        .filter((input) => !input.disabled && ((input.hasAttribute('data-lulevi-required') && input.value.trim().length === 0) || input.dataset.luleviBad !== undefined))
        .forEach((input) => {
          const field = input.closest('[data-lulevi-field]');
          field.classList.add('lulevi-field--error');
          input.setAttribute('aria-invalid', 'true');
          const error = field.querySelector('[data-lulevi-error]');
          if (error) error.hidden = false;
          if (!first) first = input;
        });
      if (!first && this.photoMissing() && this.photoField) {
        const msg = this.querySelector('[data-lulevi-photo-msg]');
        if (msg && msg.hidden) { msg.textContent = this.photoField.dataset.msgChoose || ''; msg.hidden = false; }
        this.photoField.hidden = false;
        this.photoField.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
      if (first) {
        first.focus();
        first.scrollIntoView({ block: 'center', behavior: 'smooth' });
      }
    }
  }

  customElements.define('lulevi-personalization', LuleviPersonalization);
}
